
    import { Component, Vue, Watch } from 'vue-property-decorator';
    import { mapActions, mapMutations, mapState } from 'vuex';
    import moment from 'moment';
    import { CalendarCategory, CalendarEventParsed, CalendarTimestamp } from 'vuetify';
    import TerminkalenderDetail from '@/components/Terminkalender/TerminkalenderDetail.vue';
    import TerminkalenderSucheDetail from '@/components/Terminkalender/TerminkalenderSucheDetail.vue';
    import { ValidationRules } from '@/store/types';
    import {
        Termin,
        TerminEvent,
        Terminplatz,
        Terminkalender as TerminkalenderType,
    } from '@/types/Terminkalender';
    import { SetTerminkalenderFilter } from '@/store/terminkalender';
    import Terminart from '@/types/Terminart';
    import AnmeldungenDetail from '@/components/AnmeldungenDetail.vue';
    import Anmeldung from '@/types/Anmeldung';
    import api from '@/services/Api';
    import {AxiosResponse} from 'axios';
    import Patient from '@/types/Patient';

    @Component({
        components: {
            AnmeldungenDetail,
            TerminkalenderDetail,
            TerminkalenderSucheDetail,
        },
        computed: {
            ...mapState('terminkalender', ['day', 'type', 'termin', 'kalender', 'dialog', 'searchDialog', 'loaders']),
        },
        methods: {
            ...mapActions('terminkalender', ['loadKalender', 'updateTermin', 'deleteTermin']),
            ...mapMutations('terminkalender', [
                'setDay',
                'setType',
                'setDialog',
                'setSearchDialog',
                'setFilter',
                'setTermin',
            ]),
            ...mapActions('patient', ['loadPatient']),
        },
    })
    export default class Terminkalender extends Vue {
        public $refs!: {
            calendar: Vue;
        };

        public readonly TerminartFrei = Terminart.Frei;

        public types = [
            { label: 'Monat', value: 'month' },
            { label: 'Woche', value: 'week' },
            { label: 'Tag', value: 'category' },
        ];

        public weekday = [1, 2, 3, 4, 5, 6];
        public intervalHeight = 24;
        public intervalMinutes = 15;
        public hourStart = 8;
        public hourEnd = 20;
        public intervalCount = ((this.hourEnd - this.hourStart) * (60 / this.intervalMinutes)) - 1;
        public height = '820px';

        public day!: string;
        public type!: string;
        public setDay!: (day: string) => void;
        public setType!: (type: string) => void;
        public loadPatient!: (payload: { patnr: number }) => Promise<void>;
        public rules!: ValidationRules;
        public kalender!: TerminkalenderType;
        public loadKalender!: () => void;
        public dialog!: boolean;
        public setTermin!: (termin: Termin) => void;
        public termin!: Termin;
        public setDialog!: (visible: boolean) => void;
        public setSearchDialog!: (visible: boolean) => void;
        public setFilter!: (filter: SetTerminkalenderFilter) => void;

        public events: TerminEvent[] = [];
        public dragEvent: TerminEvent | null = null;
        public createEvent: TerminEvent | null = null;
        public createStart: number | null = null;
        public extendOriginal: number | null = null;
        public dragTime: number | null = null;
        public openingPatient = false;
        public openingAnmeldung = false;
        public anmeldung = new Anmeldung();
        public anmeldungDialog = false;

        public get tag(): string {
            return this.day;
        }

        public set tag(tag: string) {
            this.setDay(tag);
        }

        public get art(): string {
            return this.type;
        }

        public set art(art: string) {
            this.setType(art);
        }

        public get categories(): CalendarCategory[] {
            return this.kalender.plaetze.map((platz: Terminplatz) => {
                const platznr = platz.platznr?.toString() ?? '';
                return {
                    name: platznr,
                    categoryName: platznr,
                };
            });
        }

        public get termine(): TerminEvent[] {
            if (this.events.length === 0) {
                this.events = [...this.kalender.events ?? []];
            }
            return this.events;
        }

        @Watch('loaders.termine')
        public onChangeLoadersTermine(isLoading: boolean): void {
            if (!isLoading && this.type !== 'month') {
                this.$nextTick(() => {
                    const [termin] = this.termine.sort((a, b) => (a.zeitStart ?? '').localeCompare(b.zeitStart ?? ''));
                    const scrollArea = this.$refs.calendar.$el.querySelector('.v-calendar-daily__scroll-area');

                    if (termin && scrollArea) {
                        const start = moment(termin.start);
                        const origin = start.clone().set({ hour: 8, minute: 0, second: 0});
                        const heightPerMinute = this.intervalHeight / this.intervalMinutes;
                        scrollArea.scrollTop = start.diff(origin, 'minutes') * heightPerMinute;
                    }
                });
            }
        }

        public mounted(): void {
            this.openingPatient = false;
            this.openingAnmeldung = false;
        }

        public startDrag({ event, timed }: { event: TerminEvent; timed: boolean }): void {
            if (event && timed) {
                this.dragEvent = event;
                this.dragTime = null;
                this.extendOriginal = null;
            }
        }

        public startTime(tms: CalendarTimestamp, event: MouseEvent): void {
            if (this.art === 'category' && !tms.category) return;
            if (this.dialog) return;

            const mouse = this.toTime(tms);

            if (this.dragEvent && this.dragTime === null) {
                const start = this.dragEvent.start;

                this.dragTime = mouse - start;
            } else {
                const defaultCategoryName = this.kalender.defaultPlatz?.platznr?.toString() ?? '';

                // https://github.com/vuetifyjs/vuetify/issues/12922
                const category = tms.category && typeof tms.category === 'object'
                    ? tms.category.categoryName ?? defaultCategoryName
                    : tms.category ?? defaultCategoryName;

                this.createStart = this.roundTime(mouse);

                this.createEvent = {
                    patient: null,
                    color: Terminart.DefaultColor,
                    start: this.createStart,
                    end: this.createStart + (15 * 60 * 1000),
                    category,
                    timed: true,
                    erschienen: 'n',
                    textcolor: Terminart.DefaultFontColor,
                    isFreierTermin: event.shiftKey,
                };

                this.events.push(this.createEvent as TerminEvent);
            }
        }

        @Watch('kalender.events')
        public onChangeKalenderEvents(events: TerminEvent[]): void {
            this.events = events;
        }

        @Watch('kalender.zeiten')
        public onChangeKalenderZeiten(zeiten: any[]): void {
            if (zeiten && zeiten.length > 0) {
                const times = zeiten.map(z => z.zeit).sort();
                const firstTime = times[0]; // e.g., "06:30"
                const lastTime = times[times.length - 1]; // e.g., "18:00"
                
                this.hourStart = parseInt(firstTime.split(':')[0], 10);
                this.hourEnd = parseInt(lastTime.split(':')[0], 10) + 1;
                this.intervalCount = ((this.hourEnd - this.hourStart) * (60 / this.intervalMinutes)) - 1;
            }
        }

        public extendBottom(event: TerminEvent): void {
            this.createEvent = event;
            this.createStart = event.start;
            this.extendOriginal = event.end;
        }

        public mouseMove(tms: CalendarTimestamp): void {
            if (this.art === 'category' && !tms.category) return;

            const mouse = this.toTime(tms);

            if (this.dragEvent && this.dragTime !== null) {
                const start = this.dragEvent.start;
                const end = this.dragEvent.end;
                const duration = end - start;
                const newStartTime = mouse - this.dragTime;
                const newStart = this.roundTime(newStartTime);
                const newEnd = newStart + duration;

                const startHour = moment(newStart, 'x').hour();
                const endHour = moment(newEnd, 'x').hour();
                if (startHour < this.hourStart || endHour >= this.hourEnd) return;

                this.dragEvent.start = newStart;
                this.dragEvent.end = newEnd;

                if (tms.category && typeof tms.category === 'object') {
                    this.dragEvent.category = tms.category.categoryName;
                } else if (tms.category === 'string') {
                    this.dragEvent.category = tms.category;
                }
            } else if (this.createEvent && this.createStart !== null) {
                const mouseRounded = this.roundTime(mouse, false);
                const min = Math.min(mouseRounded, this.createStart);
                const max = Math.max(mouseRounded, this.createStart);
                if (mouseRounded === min || mouseRounded > (min + 3 * 60 * 60 * 1000)) return;

                this.createEvent.start = min;
                this.createEvent.end = max;

                if (tms.category && typeof tms.category === 'object') {
                    this.createEvent.category = tms.category.categoryName;
                } else if (tms.category === 'string') {
                    this.createEvent.category = tms.category;
                }
            }
        }

        public onClickAnmeldung(anmeldung_id: number): void {
            this.openingAnmeldung = true;
            this.$emit('close');
            this.anmeldung = new Anmeldung({ anmeldung_id });
            this.anmeldungDialog = true;
        }

        public onClickPatient(patnr: number): void {
            this.openingPatient = true;
            this.$emit('close');
            this.$router.push('/');
            this.loadPatient({ patnr });
        }

        public resetEvent(): void {
            this.dragTime = null;
            this.dragEvent = null;
            this.createEvent = null;
            this.createStart = null;
            this.extendOriginal = null;
        }

        public endDrag({ date }: CalendarTimestamp): void {
            setTimeout(() => {
                if (this.openingPatient || this.openingAnmeldung) {
                    this.openingAnmeldung = false;
                    this.resetEvent();
                    return;
                }

                const event = this.createEvent ?? this.dragEvent;
                if (!event) return;

                const freieTerminart = (art: Terminart): boolean =>
                    art.bezeichnung?.toLocaleUpperCase() === Terminart.Frei;


                const termin = this.createEvent !== null && this.extendOriginal === null
                    ? new Termin({
                        terminart: event.isFreierTermin
                            ? this.kalender.arten.find(freieTerminart)?.terminart ?? null
                            : null
                    })
                    : new Termin({
                        ...this.kalender.termine.find((termin: Termin) => {
                            return termin.termin_id === event.termin_id;
                        }),
                    });

                const eventEnd = moment(event.end, 'x');
                const eventStart = moment(event.start, 'x');
                const diffHours = eventEnd.diff(eventStart, 'hours');
                const diffMinutes = eventEnd.diff(eventStart, 'minutes');

                const padTime = (time: number): string => time.toString().padStart(2, '0');

                termin.zeit = padTime(eventStart.hour()) + ':' + padTime(eventStart.minute());
                termin.dauer = padTime(diffHours) + ':' + padTime(diffMinutes % 60);
                termin.datum = date;
                termin.platznr = parseInt(event.category as string, 10);

                this.setTermin(termin);
                this.setDialog(true);

                this.resetEvent();
            }, 2);
        }

        public cancelDrag(): void {
            if (this.createEvent) {
                if (this.extendOriginal) {
                    this.createEvent.end = this.extendOriginal;
                } else {
                    const i = this.events.indexOf(this.createEvent);
                    if (i !== -1) {
                        this.events.splice(i, 1);
                    }
                }
            }

            this.createEvent = null;
            this.createStart = null;
            this.dragTime = null;
            this.dragEvent = null;
        }

        public roundTime(time: number, down = true): number {
            const roundTo = 15;
            const roundDownTime = roundTo * 60 * 1000;

            return down
                ? time - time % roundDownTime
                : time + (roundDownTime - (time % roundDownTime));
        }

        public toTime(tms: CalendarTimestamp): number {
            return new Date(tms.year, tms.month - 1, tms.day, tms.hour, tms.minute).getTime();
        }

        public getEventColor(event: TerminEvent): string {
            const rgb = parseInt(event.color?.substring(1) ?? '0', 16);
            const r = (rgb >> 16) & 0xFF;
            const g = (rgb >> 8) & 0xFF;
            const b = (rgb >> 0) & 0xFF;

            return event === this.dragEvent
                ? `rgba(${r}, ${g}, ${b}, 0.7)`
                : event === this.createEvent
                    ? `rgba(${r}, ${g}, ${b}, 0.7)`
                    : event.color;
        }

        public getEvents({ start, end }: CalendarEventParsed): void {
            this.setFilter({
                von: start.date,
                bis: this.type === 'category' ? start.date : end.date,
            });
            this.loadKalender();
        }

        public onResize(): void {
            this.height = (window.innerHeight - 180).toString() + 'px';
        }

        public onCloseDetail(): void {
            this.setDialog(false);
            this.events = [];
        }

        public onCloseSearchDetail(): void {
            this.setSearchDialog(false);
        }

        public onClickDate(): void {
            switch (this.type) {
                case 'week':
                    this.art = 'category';
                    break;
                case 'category':
                    this.art = 'week';
                    break;
                case 'month':
                    this.art = 'category';
                    break;
            }
        }

        public onClickEvent(event: { day: CalendarTimestamp } | CalendarTimestamp): void {
            if (this.art === 'month') {
                this.art = 'category';
                const day = typeof event.day === 'object' ? event.day : event as CalendarTimestamp;
                this.tag = day.date;
            }
        }

        public checkPatient(event: TerminEvent) {
            if (event.patnr) {
                return true;
            } else if (event.anmeldung_id) {
                api.post('ivs/patienten/getanmeldungpatient', { anmeldung_id: event.anmeldung_id })
                    .then((response: AxiosResponse) => {
                        if (response.data.body.patnr) {
                            event.patnr = response.data.body.patnr;
                        }
                    })
                    .catch(console.error);
            }
        }
    }
